import { useTheme } from '../ThemeContext';
import { useEffect, useState, useRef } from 'react';

const INDUSTRIES = [
  {
     title: 'Robotics & Automation',
    tagline: 'Proactive delay compensation for robot actuation',
    desc: 'In 5G-connected robotic systems, a 20ms latency spike can cause a welding arm to miss its target by millimeters — destroying a component worth thousands. Our Hybrid LSTM predicts these spikes 10 steps ahead, giving the control loop time to compensate before the delay materializes.',
    impact: '99.2% uptime', layer: 'IP + RLC Layer',
    details: ['Real-time trajectory adjustment','Predictive force-feedback control','Sub-millisecond proactive correction','Compatible with ROS2 over 5G'],
    color: '#00d4ff',
  },
  {
     title: 'Industry 4.0 / Smart Factory',
    tagline: 'Predictive QoS for factory floor networks',
    desc: 'Modern smart factories rely on hundreds of 5G-connected sensors and actuators operating in tight coordination. Network delay prediction enables proactive scheduling — ensuring critical commands arrive before deadlines, preventing costly production halts.',
    impact: '23% fewer stoppages', layer: 'MAC Layer QoS',
    details: ['Production line synchronization','Predictive maintenance alerting','OPC-UA over 5G integration','Multi-cell handover prediction'],
    color: '#00cc66',
  },
  {
     title: 'Autonomous Vehicles & V2X',
    tagline: 'Latency forecasting for safety-critical decisions',
    desc: 'Vehicle-to-everything (V2X) communications require sub-10ms latency for emergency braking coordination. Predicting 5G delay 10 steps ahead allows the vehicle control system to buffer messages and adjust reaction timing before communication windows degrade.',
    impact: '10ms safety margin', layer: 'Physical Layer',
    details: ['V2V emergency braking coordination','Infrastructure signal prediction','Highway platooning stability','DSRC fallback triggering'],
    color: '#f0a500',
  },
  {
    icon: '🏥', title: 'Remote Surgery / Telemedicine',
    tagline: 'Ultra-reliable delay prediction for teleoperated devices',
    desc: 'Surgical robotics over 5G requires latency below 15ms to maintain tactile feedback fidelity. Our model\'s ability to predict delay windows enables the haptic rendering system to maintain the illusion of physical presence even during transient network degradation.',
    impact: '<15ms latency budget', layer: 'End-to-End IP',
    details: ['Haptic feedback compensation','Instrument trajectory smoothing','Network quality gating','Emergency local takeover trigger'],
    color: '#ff5577',
  },
  {
    icon: '📡', title: 'Telecom Network Operations',
    tagline: 'SLA monitoring and congestion prediction',
    desc: 'Network operations centers can use delay forecasting to proactively reroute traffic before SLA thresholds are breached, rather than reacting after violations occur. This shifts telco operations from reactive firefighting to predictive management.',
    impact: '40% fewer SLA breaches', layer: 'All Layers',
    details: ['Automated SLA breach prediction','Traffic rerouting triggers','Congestion heat mapping','Per-UE delay profiling'],
    color: '#9966ff',
  },
  {
     title: 'Cloud Gaming & XR',
    tagline: 'Adaptive bitrate and input lag compensation',
    desc: 'Extended reality and cloud gaming over 5G require consistent sub-20ms round-trip latency. By predicting delay windows, the streaming engine can pre-buffer frames and adjust encoder settings before the user experiences any perceptible quality degradation.',
    impact: '60fps stability', layer: 'Application Layer',
    details: ['Frame pre-buffering strategy','Adaptive resolution scaling','Controller input interpolation','Jitter buffer optimization'],
    color: '#00ffaa',
  },
];

function RobotAnimation({ dark }) {
  const accent = dark ? '#00d4ff' : '#0078c8';
  return (
    <div style={{position:'relative',width:120,height:120,margin:'0 auto'}}>
      <style>{`
        @keyframes armSwing{0%,100%{transform:rotate(-20deg);}50%{transform:rotate(20deg);}}
        @keyframes bodyBob{0%,100%{transform:translateY(0);}50%{transform:translateY(-4px);}}
        @keyframes eyeBlink{0%,90%,100%{transform:scaleY(1);}95%{transform:scaleY(0.1);}}
        @keyframes signalPulse{0%,100%{opacity:0.3;transform:scale(0.8);}50%{opacity:1;transform:scale(1.2);}}
      `}</style>
      {/* signal waves */}
      {[1,2,3].map(i=>(
        <div key={i} style={{
          position:'absolute',top:0,left:'50%',transform:'translate(-50%,-50%)',
          width:i*36,height:i*36,borderRadius:'50%',
          border:`1px solid ${accent}`,opacity:0,
          animation:`signalPulse 2s ease-in-out ${i*0.4}s infinite`,
        }}/>
      ))}
      {/* robot body */}
      <div style={{position:'absolute',top:'30%',left:'50%',transform:'translate(-50%,0)',animation:'bodyBob 2s ease-in-out infinite'}}>
        {/* head */}
        <div style={{width:40,height:32,background:dark?'#1a3a5c':'#1e3a5f',borderRadius:6,margin:'0 auto 4px',position:'relative',border:`1px solid ${accent}55`}}>
          {/* eyes */}
          <div style={{position:'absolute',top:8,left:8,width:8,height:8,background:accent,borderRadius:'50%',animation:'eyeBlink 3s ease-in-out infinite',boxShadow:`0 0 6px ${accent}`}}/>
          <div style={{position:'absolute',top:8,right:8,width:8,height:8,background:accent,borderRadius:'50%',animation:'eyeBlink 3s ease-in-out 0.1s infinite',boxShadow:`0 0 6px ${accent}`}}/>
          {/* antenna */}
          <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',width:2,height:12,background:accent}}/>
          <div style={{position:'absolute',top:-16,left:'50%',transform:'translateX(-50%)',width:6,height:6,background:accent,borderRadius:'50%',boxShadow:`0 0 8px ${accent}`}}/>
        </div>
        {/* torso */}
        <div style={{width:50,height:36,background:dark?'#152a44':'#1a3060',borderRadius:4,margin:'0 auto',border:`1px solid ${accent}33`,position:'relative',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{width:18,height:12,background:accent+'22',border:`1px solid ${accent}55`,borderRadius:2,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{width:8,height:6,background:accent,borderRadius:1,opacity:0.8}}/>
          </div>
          {/* left arm */}
          <div style={{position:'absolute',left:-14,top:4,width:12,height:28,background:dark?'#1a3a5c':'#1e3a5f',borderRadius:4,transformOrigin:'top center',animation:'armSwing 2s ease-in-out infinite',border:`1px solid ${accent}33`}}/>
          {/* right arm */}
          <div style={{position:'absolute',right:-14,top:4,width:12,height:28,background:dark?'#1a3a5c':'#1e3a5f',borderRadius:4,transformOrigin:'top center',animation:'armSwing 2s ease-in-out 1s infinite',border:`1px solid ${accent}33`}}/>
        </div>
        {/* legs */}
        <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:4}}>
          {[0,1].map(i=>(
            <div key={i} style={{width:16,height:24,background:dark?'#1a3a5c':'#1e3a5f',borderRadius:'0 0 4px 4px',animation:`bodyBob 2s ease-in-out ${i*0.5}s infinite`,border:`1px solid ${accent}33`}}/>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Industries() {
  const { dark } = useTheme();
  const [inView, setInView] = useState({});
  const [introGone, setIntroGone] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    setTimeout(() => setIntroGone(true), 2800);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting) setInView(p=>({...p,[e.target.dataset.i]:true})); });
    }, {threshold:0.12});
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const bg = dark?'#060c18':'#f4f8ff';
  const border = dark?'rgba(0,212,255,0.13)':'rgba(0,120,200,0.13)';
  const accent = dark?'#00d4ff':'#0078c8';
  const text1 = dark?'#e8f4ff':'#0a1e3d';
  const text2 = dark?'#8ab4d4':'#3a5a7a';
  const text3 = dark?'#4a7090':'#7090b0';
  const gridC = dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)';
  const cardBg = dark?'#0d1929':'#ffffff';
  const cardShad = dark?'none':'0 2px 16px rgba(0,100,200,0.07)';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .ind-root{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;overflow-x:hidden;transition:background 0.35s;}
        .ind-root::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}
        .fade-in{opacity:0;transform:translateY(24px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}
      `}</style>

      {/* Robot intro animation */}
      {!introGone && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:dark?'#060c18':'#f4f8ff',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:24,animation:'fadeOut 0.5s ease 2.3s forwards',pointerEvents:'none'}}>
          <style>{`@keyframes fadeOut{to{opacity:0;visibility:hidden;}}`}</style>
          <RobotAnimation dark={dark}/>
          <div style={{fontFamily:"'Orbitron',monospace",fontSize:13,letterSpacing:4,color:accent,textTransform:'uppercase',opacity:0.9}}>Industry Applications</div>
          <div style={{fontFamily:"'Inter',sans-serif",fontSize:12,color:text3,letterSpacing:2}}>5G · Robotics · Industry 4.0 · Autonomous Systems</div>
        </div>
      )}

      <div className="ind-root">
        {/* Hero */}
        <div style={{position:'relative',zIndex:1,padding:'60px 24px 40px',textAlign:'center',borderBottom:`1px solid ${border}`,background:dark?'rgba(0,212,255,0.02)':'rgba(0,120,200,0.02)'}}>
          <div style={{background:dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)',border:`1px solid ${border}`,display:'inline-block',padding:'5px 18px',borderRadius:20,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:3,color:accent,textTransform:'uppercase'}}> Industry Applications</span>
          </div>
          <h1 style={{fontFamily:"'Orbitron',monospace",fontSize:'clamp(26px,5vw,52px)',fontWeight:900,color:text1,marginBottom:12,lineHeight:1.08}}>
            5G Delay Prediction<br/><span style={{color:accent}}>Across Industries</span>
          </h1>
          <p style={{fontSize:16,color:text2,maxWidth:560,margin:'0 auto',lineHeight:1.7}}>
            The Hybrid LSTM model enables proactive latency management across six critical sectors where milliseconds determine outcomes.
          </p>
          <div style={{marginTop:32}}>
            <RobotAnimation dark={dark}/>
          </div>
        </div>

        {/* Industry cards */}
        <div style={{position:'relative',zIndex:1,maxWidth:1100,margin:'0 auto',padding:'60px 24px 80px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:24}}>
            {INDUSTRIES.map((ind,i)=>(
              <div
                key={ind.title}
                data-i={`ind${i}`}
                ref={el=>{refs.current[`ind${i}`]=el;}}
                className={`fade-in${inView[`ind${i}`]?' vis':''}`}
                style={{transitionDelay:`${i*0.08}s`}}
              >
                <div style={{
                  border:`1px solid ${dark?`${ind.color}22`:`${ind.color}22`}`,
                  background:cardBg,borderRadius:8,padding:28,
                  boxShadow:cardShad,
                  transition:'all 0.3s',cursor:'default',
                  position:'relative',overflow:'hidden',
                }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=ind.color+'66';e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow=`0 12px 40px ${ind.color}18`;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=dark?`${ind.color}22`:`${ind.color}22`;e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=cardShad;}}
                >
                  <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${ind.color},transparent)`}}/>
                  <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:16}}>
                    <div style={{fontSize:32}}>{ind.icon}</div>
                    <div>
                      <div style={{fontFamily:"'Orbitron',monospace",fontSize:13,fontWeight:700,color:text1,marginBottom:3}}>{ind.title}</div>
                      <div style={{fontSize:11,color:ind.color,letterSpacing:0.5}}>{ind.tagline}</div>
                    </div>
                  </div>
                  <p style={{fontSize:13,color:text2,lineHeight:1.7,marginBottom:18}}>{ind.desc}</p>
                  <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
                    <span style={{fontSize:11,fontWeight:600,color:ind.color,background:`${ind.color}12`,border:`1px solid ${ind.color}33`,padding:'3px 10px',borderRadius:12}}>{ind.impact}</span>
                    <span style={{fontSize:11,color:text3,background:dark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.04)',border:`1px solid ${border}`,padding:'3px 10px',borderRadius:12}}>{ind.layer}</span>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {ind.details.map(d=>(
                      <div key={d} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:text2}}>
                        <div style={{width:5,height:5,borderRadius:'50%',background:ind.color,flexShrink:0}}/>
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
